// Trigger sets — authored per report; see docs/builder-report-authoring-spec.md.
// Batch "coshhDusts": coshh-assessment (general), silica-dust, cement-concrete.
import type { TriggerQuestion } from "./types";
import { SILICA, MANUAL_HANDLING, HAVS, COSHH_DETAIL_QS } from "./shared";

// Canonical, already-minted general COSHH hazard id (reused, not re-minted).
const COSHH_GENERAL = "haz-coshh-coshh-hazardou-substance-exposure";

export const COSHH_DUSTS_TRIGGERS: Record<string, TriggerQuestion[]> = {
  "coshh-assessment": [
    {
      key: "substancesNamed",
      label: "Will the task use products carrying a hazard pictogram or hazard statement?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [COSHH_GENERAL] },
        no: {
          hazardsOff: [COSHH_GENERAL],
          scopeLine: "No products carrying a hazard pictogram or hazard statement are in use — confirmed against the Safety Data Sheets; COSHH controls are not required for this task.",
        },
      },
      // COSHH on a job that genuinely uses no hazardous product is rare; keep it
      // on and fail the check until the SDS review confirms otherwise.
      notSure: { hazardsOn: [COSHH_GENERAL], failCheck: "Hazardous substances in use confirmed against the SDSs" },
      followUps: { when: "yes", questions: COSHH_DETAIL_QS },
    },
    {
      key: "decantMix",
      label: "Will substances be decanted, mixed or diluted on site?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-coshh-decanting-mixing-vapour-or-splash"] },
        no: {
          hazardsOff: ["haz-coshh-decanting-mixing-vapour-or-splash"],
          sequenceOff: [6],
          scopeLine: "Products are used as supplied — no on-site decanting, mixing or diluting; vapour and splash controls for decanting are not required.",
        },
      },
      notSure: { hazardsOn: ["haz-coshh-decanting-mixing-vapour-or-splash"], failCheck: "On-site decanting / mixing confirmed" },
    },
    {
      key: "storedOnSite",
      label: "Will substances be stored on site overnight (more than the day's quantity)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-coshh-incompatible-substance-storage-and-segregation"] },
        no: {
          hazardsOff: ["haz-coshh-incompatible-substance-storage-and-segregation"],
          sequenceOff: [4],
          scopeLine: "Only the day's quantity is brought to site and removed at the end of the shift — no overnight storage, so segregation-store controls are not required.",
        },
      },
      notSure: { hazardsOn: ["haz-coshh-incompatible-substance-storage-and-segregation"], failCheck: "On-site storage arrangements confirmed" },
    },
    {
      key: "spillRisk",
      label: "Are liquids or flowable substances used that could spill?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-coshh-spill-or-uncontrolled-release"] },
        no: {
          hazardsOff: ["haz-coshh-spill-or-uncontrolled-release"],
          emergencyOff: ["Spill or uncontrolled release"],
          sequenceOff: [7],
          scopeLine: "Substances are solid or paste form with no spill potential — liquid spill-response controls are not required for this task.",
        },
      },
      notSure: { hazardsOn: ["haz-coshh-spill-or-uncontrolled-release"], failCheck: "Spill potential confirmed" },
    },
    {
      key: "repeatExposure",
      label: "Is exposure regular or repeated (could cause dermatitis, sensitisation or a lung condition)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-coshh-health-surveillance-trigger-missed"] },
        no: {
          hazardsOff: ["haz-coshh-health-surveillance-trigger-missed"],
          sequenceOff: [9],
          scopeLine: "Exposure is one-off or infrequent and below the level at which an identifiable disease is reasonably likely — health surveillance is not triggered for this task.",
        },
      },
      notSure: { hazardsOn: ["haz-coshh-health-surveillance-trigger-missed"], failCheck: "Health surveillance trigger reviewed under COSHH Reg 11" },
    },
    {
      key: "faceFit",
      label: "Face-fit certificate — record reference and expiry for tight-fitting RPE (or N/A if none used)",
      kind: "text",
      placeholder: "e.g. FFP3, J Smith, face-fit cert 04/2026; or N/A — no tight-fitting RPE",
      requiredCheck: "Face-fit certificate reference recorded (or N/A confirmed)",
      effects: { "*": { scopeLine: "RPE / face-fit: {answer}." } },
    },
  ],

  "silica-dust": [
    {
      key: "dustControl",
      label: "How will dust be controlled at source?",
      kind: "choice",
      options: ["Water suppression", "On-tool extraction (M-class)", "On-tool extraction (H-class)", "Both water and on-tool extraction"],
      effects: {
        "Water suppression": { scopeLine: "Dust control at source: water suppression on the tool, run continuously during cutting." },
        "On-tool extraction (M-class)": { scopeLine: "Dust control at source: M-class on-tool extraction matched to the tool, airflow checked before the shift." },
        "On-tool extraction (H-class)": { scopeLine: "Dust control at source: H-class on-tool extraction matched to the tool, airflow checked before the shift." },
        "Both water and on-tool extraction": { scopeLine: "Dust control at source: water suppression and on-tool extraction used together." },
      },
    },
    {
      key: "breaking",
      label: "Will breaking, scabbling or hand-held breakers be used (vibration)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [HAVS] },
        no: {
          hazardsOff: [HAVS],
          scopeLine: "No breaking, scabbling or hand-held breakers — work is cutting/chasing/drilling only; hand-arm vibration controls are not required for this task.",
        },
      },
      notSure: { hazardsOn: [HAVS], failCheck: "Vibrating-tool use confirmed" },
    },
    {
      key: "regularExposure",
      label: "Is silica exposure regular or foreseeable for these operatives?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-dust-fumes-silica-health-surveillance-trigger-missed"] },
        no: {
          hazardsOff: ["haz-dust-fumes-silica-health-surveillance-trigger-missed"],
          sequenceOff: [7],
          scopeLine: "Exposure is one-off and below the level at which surveillance is triggered — confirmed by the dust assessment; health surveillance is not required for this task.",
        },
      },
      // Silica disease is irreversible — keep surveillance on if unsure.
      notSure: { hazardsOn: ["haz-dust-fumes-silica-health-surveillance-trigger-missed"], failCheck: "Regular silica exposure reviewed for health surveillance under COSHH Reg 11" },
    },
    {
      key: "hotWork",
      label: "Will cutting generate sparks near combustibles?",
      kind: "yesno",
      effects: {
        yes: { permitsAdd: ["Hot work permit"], scopeLine: "Spark-generating cutting near combustibles — a hot work permit and fire watch apply." },
        no: { scopeLine: "No spark-generating cutting near combustibles — a hot work permit is not required for this task." },
      },
      notSure: { permitsAdd: ["Hot work permit"], failCheck: "Spark / combustible proximity confirmed" },
    },
    {
      key: "monitoring",
      label: "Exposure check — record how exposure is kept below the 0.1 mg/m³ WEL (control basis or monitoring result)",
      kind: "text",
      placeholder: "e.g. wet cut + FFP3 APF20, manufacturer dust data; or air monitoring 0.04 mg/m³ (2025)",
      requiredCheck: "Basis for staying below the 0.1 mg/m³ RCS WEL recorded",
      effects: { "*": { scopeLine: "Exposure control basis: {answer}." } },
    },
    {
      key: "faceFit",
      label: "Face-fit certificate — record reference and expiry for the RPE in use",
      kind: "text",
      placeholder: "e.g. FFP3, J Smith, face-fit cert 04/2026",
      requiredCheck: "Face-fit certificate reference recorded",
      effects: { "*": { scopeLine: "RPE / face-fit: {answer}." } },
    },
  ],

  "cement-concrete": [
    {
      key: "wetContact",
      label: "Will the crew kneel, stand in or have prolonged skin contact with wet mix?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-coshh-alkaline-burn-from-prolonged-wet-concrete-skin-contact"], requireFields: ["welfareArrangements"] },
        // The burn hazard is intrinsic to cement work (incidental splash still
        // burns) — "no" narrows the scope note but never removes the hazard.
        no: {
          emergencyOff: ["Suspected chemical burn from trapped mix"],
          scopeLine: "The crew works from outside the mix with no kneeling or standing in wet concrete — prolonged-contact burn controls still apply to incidental splash but trapped-mix kneeling controls are not the primary case.",
        },
      },
      // Alkaline burns are severe and often painless — keep the hazard on if unsure.
      notSure: { hazardsOn: ["haz-coshh-alkaline-burn-from-prolonged-wet-concrete-skin-contact"], failCheck: "Prolonged wet-concrete contact confirmed" },
    },
    {
      key: "dryCement",
      label: "Will dry cement be opened, mixed or batched on site (dust)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-coshh-irritant-cement-dust-from-dry-mixing"] },
        no: {
          hazardsOff: ["haz-coshh-irritant-cement-dust-from-dry-mixing"],
          sequenceOff: [2],
          scopeLine: "Ready-mixed concrete is delivered to site — no dry cement is opened or batched, so dry-cement dust controls are not required for this task.",
        },
      },
      notSure: { hazardsOn: ["haz-coshh-irritant-cement-dust-from-dry-mixing"], failCheck: "Dry-cement handling confirmed" },
    },
    {
      key: "eyeRisk",
      label: "Could cement or wet mix splash into the eyes?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-coshh-cement-eye-chemical-burn"] },
        no: {
          hazardsOff: ["haz-coshh-cement-eye-chemical-burn"],
          emergencyOff: ["Cement in the eye"],
          scopeLine: "No foreseeable splash to the eyes for this task — eye-burn controls are limited to general eye protection.",
        },
      },
      notSure: { hazardsOn: ["haz-coshh-cement-eye-chemical-burn"], failCheck: "Eye-splash potential confirmed" },
    },
    {
      key: "repeatCrew",
      label: "Is this a repeat-exposure crew (regular cement / concrete work)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-coshh-chromium-vi-sensitisation-allergic-dermatitis"] },
        no: {
          hazardsOff: ["haz-coshh-chromium-vi-sensitisation-allergic-dermatitis"],
          scopeLine: "One-off or occasional cement contact — chromium VI sensitisation surveillance is not triggered, though skin protection and fresh cement still apply.",
        },
      },
      notSure: { hazardsOn: ["haz-coshh-chromium-vi-sensitisation-allergic-dermatitis"], failCheck: "Repeat cement exposure reviewed for chromium VI sensitisation" },
    },
    {
      key: "heavyHandling",
      label: "Will cement bags or skips of mix be handled by hand?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [MANUAL_HANDLING] },
        no: {
          hazardsOff: [MANUAL_HANDLING],
          scopeLine: "Placement is by pump or mechanical means with no manual handling of bags or mix — manual handling controls are not required for this task.",
        },
      },
      notSure: { hazardsOn: [MANUAL_HANDLING], failCheck: "Manual handling of cement / mix confirmed" },
    },
    {
      key: "freshness",
      label: "Cement freshness — record the supply/use-by check that keeps chromium VI below the limit",
      kind: "text",
      placeholder: "e.g. bagged cement within manufacturer's shelf life, stock rotated; batch date checked",
      requiredCheck: "Cement shelf-life / chromate-reducer check recorded",
      effects: { "*": { scopeLine: "Cement freshness check: {answer}." } },
    },
    {
      key: "washing",
      label: "Washing facilities — record what is provided at the work face for immediate skin decontamination",
      kind: "text",
      placeholder: "e.g. forearm-width basins, warm water, soap, towels and eyewash at the pour",
      requiredCheck: "Washing facilities at the work face recorded",
      effects: { "*": { scopeLine: "Washing facilities: {answer}." } },
    },
  ],
};
